import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { observer } from "mobx-react-lite";
import Dialog from "@components/Dialog";
import Text from "@components/Text";
import { Row, Column } from "@components/Flex";
import Button from "@components/Button";
import Select, { TOption } from "@components/Select";
import Loading from "@components/Loading";
import Checkbox from "@components/Checkbox";
import SearchInput from "@components/SearchInput";
import { ReactComponent as SearchIcon } from "@assets/icons/search.svg";
import { useLang } from "@src/hooks/useLang";
import {
  getJohnDeereOrganizations,
  getJohnDeereMachines,
  importJohnDeereMachines,
  JohnDeereMachine,
  JohnDeereOrganization,
  safeDeereUrl,
} from "@services/johnDeereService";
import {
  allBulkSelected,
  bulkSelectable,
  isSelectable,
  machineImportErrorText,
  toggleAllSelection,
} from "./jdMachinePicker";

interface IProps {
  visible: boolean;
  onClose: () => void;
  /** Called after at least one machine was imported (re-sync the device list). */
  onSuccess: () => void;
}

/** Keys added with the 2026-10-20 review; optional until every locale has them. */
type NewDevicesKeys =
  | "importJohnDeereHintDeere"
  | "importJohnDeereErrorNotFound"
  | "importJohnDeereErrorFailed"
  | "importJohnDeereLoadFailed"
  | "importJohnDeereFailed";

/**
 * "Import from John Deere" on the Devices screen (feature config
 * `john_deere_combines`): pick combines from an Operations Center organization;
 * each becomes a device whose harvest data the backend pulls from Deere.
 * Only opened while John Deere is connected (the button checks), so there is
 * no connect step here.
 */
const ImportJohnDeereMachinesDialog: React.FC<IProps> = ({ visible, onClose, onSuccess }) => {
  const lang = useLang();
  const jdLang = lang.johnDeere;
  const t = lang.devices as typeof lang.devices & Partial<Record<NewDevicesKeys, string>>;

  const [organizations, setOrganizations] = useState<JohnDeereOrganization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState("");
  const [machines, setMachines] = useState<JohnDeereMachine[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showAll, setShowAll] = useState(false);
  const [query, setQuery] = useState("");
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [machinesLoading, setMachinesLoading] = useState(false);
  // The list failed to load: what is ticked may not be in this org any more.
  const [machinesError, setMachinesError] = useState(false);
  const [importing, setImporting] = useState(false);
  const [connectionsUrl, setConnectionsUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    setOrgsLoading(true);
    setSelectedOrg("");
    setMachines([]);
    setSelected(new Set());
    setQuery("");
    setShowAll(false);
    getJohnDeereOrganizations()
      .then((r) => {
        if (cancelled) return;
        const orgs = r.organizations ?? [];
        setOrganizations(orgs);
        setConnectionsUrl(r.connectionRequired ? safeDeereUrl(r.connectionsUrl) : null);
        // One organization (the usual farm): skip the picker step.
        if (orgs.length === 1) setSelectedOrg(orgs[0].selfUri);
      })
      .catch((e: any) => {
        if (cancelled) return;
        const url = safeDeereUrl(e?.connectionsUrl);
        if (url) setConnectionsUrl(url);
      })
      .finally(() => !cancelled && setOrgsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [visible]);

  useEffect(() => {
    if (!visible || !selectedOrg) return;
    let cancelled = false;
    // A new org or "Show all" is a new list: ids ticked in the old one would be
    // posted to this org and come back as "not found" (or import a tractor that
    // is no longer on screen). `t`, the locale object, changes only with the
    // language.
    setSelected(new Set());
    setMachines([]);
    setMachinesError(false);
    setMachinesLoading(true);
    getJohnDeereMachines(selectedOrg, showAll)
      .then((r) => {
        if (cancelled) return;
        setMachines(r.machines ?? []);
        setSelected(new Set());
      })
      .catch((e: any) => {
        if (cancelled) return;
        setMachines([]);
        setMachinesError(true);
        const connections = safeDeereUrl(e?.connectionsUrl);
        const terms = safeDeereUrl(e?.termsLocation);
        if (connections) setConnectionsUrl(connections);
        else if (terms) window.open(terms, "_blank", "noopener,noreferrer");
        else {
          // The backend's text is English and technical; the customer gets the
          // localised one.
          console.error("John Deere machines failed to load", e);
          toast.error(t.importJohnDeereLoadFailed ?? "Could not load machines from John Deere. Please try again.");
        }
      })
      .finally(() => !cancelled && setMachinesLoading(false));
    return () => {
      cancelled = true;
    };
  }, [visible, selectedOrg, showAll, t]);

  const visibleMachines = useMemo(() => {
    const q = query.trim().toLowerCase();
    return machines.filter(
      (m) =>
        !q ||
        m.name.toLowerCase().includes(q) ||
        (m.model ?? "").toLowerCase().includes(q) ||
        (m.vin ?? "").toLowerCase().includes(q)
    );
  }, [machines, query]);

  // Select all ticks combines only, also with "Show all" on (bulkSelectable).
  const canSelectAll = bulkSelectable(visibleMachines).length > 0;
  const allSelected = allBulkSelected(selected, visibleMachines);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () => setSelected((prev) => toggleAllSelection(prev, visibleMachines));

  const handleImport = async () => {
    if (!selectedOrg || selected.size === 0) return;
    setImporting(true);
    try {
      const result = await importJohnDeereMachines(selectedOrg, Array.from(selected));
      const count = result.imported?.length ?? 0;
      const errorLabels = {
        notFound: t.importJohnDeereErrorNotFound ?? "Not found in this John Deere organization",
        elsewhere: t.importJohnDeereElsewhere,
        failed: t.importJohnDeereErrorFailed ?? "Could not be imported. Please try again.",
      };
      (result.errors ?? []).forEach((err) => {
        const name = machines.find((m) => m.equipmentId === err.equipmentId)?.name ?? err.equipmentId;
        toast.error(`${name}: ${machineImportErrorText(err, errorLabels)}`);
      });
      if (count > 0) {
        toast.success(t.importJohnDeereSuccess.replace("{n}", String(count)));
        onSuccess();
        onClose();
      }
    } catch (e: any) {
      const connections = safeDeereUrl(e?.connectionsUrl);
      if (connections) setConnectionsUrl(connections);
      else {
        console.error("John Deere machine import failed", e);
        toast.error(t.importJohnDeereFailed ?? "Import failed. Please try again.");
      }
    } finally {
      setImporting(false);
    }
  };

  const orgOptions: TOption[] = organizations.map((o) => ({ value: o.selfUri, title: o.name }));
  const canImport = !!selectedOrg && selected.size > 0 && !importing && !machinesLoading && !machinesError;

  return (
    <Dialog title={t.importJohnDeereTitle} visible={visible} onClose={onClose} style={{ maxWidth: 520 }}>
      <Column crossAxisSize="max" style={{ gap: 16 }}>
        <Text size={14} color="#6B7280">
          {t.importJohnDeereHintDeere ??
            "Combines from your Operations Center become devices here. Their harvest data is pulled from John Deere every few hours and shown as John Deere recorded it, unless the combine has a GreenGrowth calibration."}
        </Text>

        {connectionsUrl && (
          <div
            style={{
              background: "#EFF6FF",
              border: "1px solid #3B82F6",
              borderRadius: 8,
              padding: 12,
            }}
          >
            <Text size={13} color="#1E40AF" style={{ display: "block", marginBottom: 8 }}>
              {t.importJohnDeerePermission}
            </Text>
            <a
              href={connectionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontWeight: 600, textDecoration: "underline", color: "#1D4ED8" }}
            >
              {t.importJohnDeereGrantLink}
            </a>
          </div>
        )}

        {organizations.length !== 1 && (
          <div>
            <Text size={14} style={{ marginBottom: 4, fontWeight: 600 }}>
              {jdLang.organization}
            </Text>
            <Select
              value={selectedOrg}
              onChange={setSelectedOrg}
              options={orgsLoading ? [] : orgOptions}
              renderValue={(opt) => opt?.title || jdLang.selectOrg}
            />
          </div>
        )}

        {selectedOrg && (
          <div>
            <Row justifyContent="space-between" alignItems="center" style={{ marginBottom: 8, gap: 8 }}>
              <div
                style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
                onClick={() => setShowAll((v) => !v)}
              >
                <span onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={showAll} onChange={setShowAll} />
                </span>
                <Text size={14} fitContent>
                  {t.importJohnDeereShowAll}
                </Text>
              </div>
              {canSelectAll && (
                <Button kind="outline" onClick={toggleAll} style={{ padding: "4px 12px", maxWidth: "fit-content" }}>
                  {allSelected ? jdLang.deselectAll : jdLang.selectAll}
                </Button>
              )}
            </Row>
            {!machinesLoading && machines.length > 5 && (
              <SearchInput
                style={{ width: "100%", marginBottom: 8 }}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t.importJohnDeereSearch}
                icon={<SearchIcon />}
              />
            )}
            <div
              style={{
                maxHeight: 280,
                overflowY: "auto",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: 8,
                flexShrink: 0,
              }}
            >
              {machinesLoading ? (
                <Loading />
              ) : visibleMachines.length === 0 ? (
                <Text size={14} color="#6B7280">
                  {t.importJohnDeereNoMachines}
                </Text>
              ) : (
                visibleMachines.map((m) => {
                  const enabled = isSelectable(m);
                  const subtitle = [m.model, m.vin].filter(Boolean).join(" · ");
                  return (
                    <div
                      key={m.equipmentId}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "8px 0",
                        cursor: enabled ? "pointer" : "default",
                        opacity: enabled ? 1 : 0.55,
                      }}
                      onClick={() => enabled && toggle(m.equipmentId)}
                    >
                      {enabled ? (
                        <span onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selected.has(m.equipmentId)}
                            onChange={() => toggle(m.equipmentId)}
                          />
                        </span>
                      ) : (
                        <span style={{ width: 20, flexShrink: 0 }} />
                      )}
                      <Column crossAxisSize="max">
                        <Text size={14} style={{ fontWeight: 600 }}>
                          {m.name}
                        </Text>
                        {(subtitle || !enabled) && (
                          <Text size={12} color="#6B7280">
                            {[subtitle, m.imported ? t.importJohnDeereAlready : m.importedElsewhere ? t.importJohnDeereElsewhere : ""]
                              .filter(Boolean)
                              .join(" · ")}
                          </Text>
                        )}
                      </Column>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        <Row justifyContent="flex-end" style={{ gap: 8, marginTop: 8 }}>
          <Button onClick={onClose} kind="outline">
            {jdLang.cancel}
          </Button>
          <Button onClick={handleImport} disabled={!canImport}>
            {/* The count includes ticks hidden by the search box. */}
            {importing ? (
              <Loading />
            ) : selected.size > 0 ? (
              `${lang.fields.importButton} (${selected.size})`
            ) : (
              lang.fields.importButton
            )}
          </Button>
        </Row>
      </Column>
    </Dialog>
  );
};

export default observer(ImportJohnDeereMachinesDialog);
